import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import {
  NOMBRE_ESTADO_CUENTA,
  NOMBRE_ESTADO_CUOTA,
  NOMBRE_ESTADO_STOCK,
} from '@libreta/contratos';
import { ErrorDeApi } from '../api/cliente';
import { iniciales, monto } from '../comun/formato';
import { Boton } from './Controles';
import { Icono } from './Icono';

// --- Estados: siempre color + ícono + palabra --------------------------------------

type Tono = 'pagado' | 'por-vencer' | 'vencido' | 'pendiente' | 'anulado' | 'informacion';

const TONO_CUOTA = { PAGADA: 'pagado', VENCIDA: 'vencido', POR_VENCER: 'por-vencer', PENDIENTE: 'pendiente' } as const;
const ICONO_TONO: Record<Tono, string> = {
  pagado: 'check', vencido: 'alerta', 'por-vencer': 'reloj', pendiente: 'calendario', anulado: 'anular', informacion: 'hoja',
};

export function Insignia({ tono, children, icono }: { tono: Tono; children: ReactNode; icono?: string }) {
  return (
    <span className={`insignia insignia--${tono}`}>
      <Icono nombre={icono ?? ICONO_TONO[tono]} />
      {children}
    </span>
  );
}

export function EstadoCuota({ estado, dias }: { estado: keyof typeof NOMBRE_ESTADO_CUOTA; dias?: number }) {
  return (
    <Insignia tono={TONO_CUOTA[estado]}>
      {NOMBRE_ESTADO_CUOTA[estado]}
      {estado === 'VENCIDA' && dias ? ` · ${dias} d` : ''}
    </Insignia>
  );
}

export function EstadoCuenta({ estado }: { estado: keyof typeof NOMBRE_ESTADO_CUENTA }) {
  const tono: Tono =
    estado === 'PAGADA' || estado === 'SIN_DEUDA' ? 'pagado' : estado === 'ATRASADA' ? 'vencido' : estado === 'ANULADA' ? 'anulado' : 'informacion';
  const icono = estado === 'AL_DIA' ? 'check' : undefined;
  return <Insignia tono={tono} icono={icono}>{NOMBRE_ESTADO_CUENTA[estado]}</Insignia>;
}

export function EstadoStock({ estado }: { estado: keyof typeof NOMBRE_ESTADO_STOCK }) {
  const tono: Tono = estado === 'OK' ? 'pagado' : estado === 'BAJO' ? 'por-vencer' : estado === 'SIN_CONTROL' ? 'pendiente' : 'vencido';
  const icono = estado === 'SIN_CONTROL' ? 'inventario' : undefined;
  return <Insignia tono={tono} icono={icono}>{NOMBRE_ESTADO_STOCK[estado]}</Insignia>;
}

// --- Montos ----------------------------------------------------------------------------

export function Monto({ valor, moneda = 'PYG', tono, fuerte }: { valor: number; moneda?: string; tono?: 'vencido' | 'pagado' | 'debil'; fuerte?: boolean }) {
  return <span className={`monto ${tono ? `monto--${tono}` : ''} ${fuerte ? 'monto--fuerte' : ''}`}>{monto(valor, moneda)}</span>;
}

// --- Estructura de página ------------------------------------------------------------------

export function Encabezado({ titulo, subtitulo, icono, acciones, children }: { titulo: string; subtitulo?: ReactNode; icono?: string; acciones?: ReactNode; children?: ReactNode }) {
  return (
    <header className="encabezado">
      <div className="encabezado__fila">
        <div className="encabezado__titulo">
          {icono ? <span className="encabezado__icono"><Icono nombre={icono} /></span> : null}
          <div>
            <h1>{titulo}</h1>
            {subtitulo ? <p>{subtitulo}</p> : null}
          </div>
        </div>
        {acciones ? <div className="encabezado__acciones">{acciones}</div> : null}
      </div>
      {children}
    </header>
  );
}

export function Panel({ titulo, icono, acciones, children, clase, sinRelleno, estilo }: { titulo?: ReactNode; icono?: string; acciones?: ReactNode; children: ReactNode; clase?: string; sinRelleno?: boolean; estilo?: CSSProperties }) {
  return (
    <section className={`panel ${sinRelleno ? 'panel--sin-relleno' : ''} ${clase ?? ''}`} style={estilo}>
      {titulo ? (
        <header className="panel__cabecera">
          <h2>{icono ? <Icono nombre={icono} /> : null}{titulo}</h2>
          {acciones ? <div className="panel__acciones">{acciones}</div> : null}
        </header>
      ) : null}
      <div className="panel__cuerpo">{children}</div>
    </section>
  );
}

export function Dato({ etiqueta, children, destacado }: { etiqueta: string; children: ReactNode; destacado?: boolean }) {
  return (
    <div className={`dato ${destacado ? 'dato--destacado' : ''}`}>
      <dt>{etiqueta}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export function Avatar({ nombre, tono }: { nombre: string; tono?: 'vencido' | 'pagado' }) {
  return <span className={`avatar ${tono ? `avatar--${tono}` : ''}`} aria-hidden>{iniciales(nombre)}</span>;
}

// --- Estados de carga ------------------------------------------------------------------

export function Cargando({ filas = 4 }: { filas?: number }) {
  return (
    <div className="esqueleto" aria-busy="true" aria-label="Cargando">
      {Array.from({ length: filas }, (_, i) => <span key={i} className="esqueleto__linea" style={{ '--i': i } as CSSProperties} />)}
    </div>
  );
}

export function Vacio({ icono = 'hoja', titulo, texto, accion }: { icono?: string; titulo: string; texto?: string; accion?: ReactNode }) {
  return (
    <div className="vacio">
      <span className="vacio__icono"><Icono nombre={icono} /></span>
      <strong>{titulo}</strong>
      {texto ? <p>{texto}</p> : null}
      {accion}
    </div>
  );
}

export function ErrorCarga({ error, reintentar }: { error: unknown; reintentar?: () => void }) {
  const mensaje = error instanceof ErrorDeApi ? error.message : 'No se pudo cargar. Revisá la conexión y probá de nuevo.';
  return (
    <div className="vacio vacio--error" role="alert">
      <span className="vacio__icono"><Icono nombre="alerta" /></span>
      <strong>{mensaje}</strong>
      {reintentar ? <Boton icono="cambiar" onClick={reintentar}>Reintentar</Boton> : null}
    </div>
  );
}

export function mensajeDeError(error: unknown): string {
  return error instanceof ErrorDeApi ? error.message : 'No se pudo completar la operación. Probá de nuevo.';
}

// --- Cajón lateral ---------------------------------------------------------------------------

/**
 * El detalle va AL COSTADO, nunca abajo ni en un modal (criterio de Rodrigo en PLES): sin
 * telón, para poder saltar de una fila a otra sin cerrarlo.
 */
export function Cajon({ abierto, alCerrar, titulo, subtitulo, children, pie, ancho }: { abierto: boolean; alCerrar: () => void; titulo: ReactNode; subtitulo?: ReactNode; children: ReactNode; pie?: ReactNode; ancho?: boolean }) {
  useEffect(() => {
    if (!abierto) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') alCerrar();
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [abierto, alCerrar]);

  if (!abierto) return null;
  return (
    <aside className={`cajon ${ancho ? 'cajon--ancho' : ''}`} role="dialog" aria-modal="false" aria-label={typeof titulo === 'string' ? titulo : undefined}>
      <header className="cajon__cabecera">
        <div>
          <h2>{titulo}</h2>
          {subtitulo ? <p>{subtitulo}</p> : null}
        </div>
        <Boton variante="fantasma" icono="cerrar" onClick={alCerrar} aria-label="Cerrar" />
      </header>
      <div className="cajon__cuerpo">{children}</div>
      {pie ? <footer className="cajon__pie">{pie}</footer> : null}
    </aside>
  );
}

/** Filas por página en todas las tablas. Pedido de Rodrigo: las tablas van paginadas SIEMPRE. */
export const POR_PAGINA = 20;

/**
 * Paginación del lado del cliente para listas que la API devuelve completas porque son
 * cortas por naturaleza (roles, medios de pago, planes). Las que crecen con la operación
 * (ventas, pagos, cuotas, productos, facturas) se paginan en la API.
 */
export function usePaginacion<T>(filas: readonly T[] | undefined, porPagina = POR_PAGINA) {
  const [pagina, setPagina] = useState(1);
  const total = filas?.length ?? 0;
  const ultima = Math.max(1, Math.ceil(total / porPagina));
  const actual = Math.min(pagina, ultima);
  return {
    pagina: actual,
    setPagina,
    porPagina,
    total,
    visibles: (filas ?? []).slice((actual - 1) * porPagina, actual * porPagina),
  };
}

export function Paginador({ pagina, porPagina, total, alCambiar }: { pagina: number; porPagina: number; total: number; alCambiar: (pagina: number) => void }) {
  const paginas = Math.max(1, Math.ceil(total / porPagina));
  if (total === 0) return null;
  return (
    <nav className="paginador" aria-label="Páginas">
      <span>{(pagina - 1) * porPagina + 1}–{Math.min(total, pagina * porPagina)} de {total}</span>
      <Boton variante="fantasma" tamano="chico" icono="izquierda" disabled={pagina <= 1} onClick={() => alCambiar(pagina - 1)} aria-label="Página anterior" />
      <Boton variante="fantasma" tamano="chico" icono="derecha" disabled={pagina >= paginas} onClick={() => alCambiar(pagina + 1)} aria-label="Página siguiente" />
    </nav>
  );
}

export function SubNav<T extends string>({ opciones, valor, alCambiar }: { opciones: Array<{ valor: T; texto: string; icono?: string; cuenta?: number }>; valor: T; alCambiar: (valor: T) => void }) {
  return (
    <nav className="subnav" aria-label="Secciones">
      {opciones.map((o) => (
        <button key={o.valor} type="button" className={`subnav__opcion ${valor === o.valor ? 'subnav__opcion--activa' : ''}`} aria-current={valor === o.valor ? 'page' : undefined} onClick={() => alCambiar(o.valor)}>
          {o.icono ? <Icono nombre={o.icono} /> : null}
          {o.texto}
          {o.cuenta !== undefined ? <span className="subnav__cuenta">{o.cuenta}</span> : null}
        </button>
      ))}
    </nav>
  );
}
